import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import GuidedTour from '../common/GuidedTour';
import { 
  Package, 
  User, 
  FileText, 
  Calendar,
  CheckCircle,
  AlertCircle,
  XCircle,
  Plus,
  Receipt,
  QrCode,
  KeyRound
} from 'lucide-react';
import { EnhancedHeader } from '../common/EnhancedHeader';
import { NotificationProvider } from '../common/NotificationSystem';
import { SettingsModal } from '../common/SettingsModal';
import { StatusBadge, DataTable, ColumnDef, OrderIdBadge } from '../shared';
import { formatDate, formatCurrency } from '../../utils';
import { MessagingPage } from './MessagingPage';
import { MessageCircle } from 'lucide-react';
import ProfileCompletion from '../ProfileCompletion';
import { API_BASE } from '@/config/api';
import { isChatEnabled } from '@/config/features';
import { dataPreloader } from '@/services/dataPreloader';
import { cacheHelpers } from '@/services/sessionCache';
import { OrderPaymentModal } from './OrderPaymentModal';
import ReceiptsTab from '../shared/ReceiptsTab';

interface Order {
  id: string;
  order_number?: string; // Add order_number field
  garmentType: string;
  description: string;
  status: 'new' | 'in_progress' | 'completed' | 'delivered' | 'cancelled' | 'appointment_scheduled';
  orderDate: string;
  dueDate: string;
  amount: number;
  tailor: string | { name?: string; customer_name?: string; phone?: string; customer_phone?: string };
  measurements: string[];
  progressSteps?: any[];
  appointment_time?: string;
  appointment_date?: string;
  appointment_scheduled?: boolean;
  customer_name?: string; // Add customer name field
  customer_phone?: string; // Add customer phone field
}

interface Measurement {
  id: string;
  name: string;
  date: string;
  status: 'complete' | 'partial' | 'pending';
  notes: string;
  measurementsCount: number;
  totalMeasurements: number;
}

const CustomerDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Initialize activeTab from URL params or localStorage, default to 'orders'
  const [activeTab, setActiveTab] = useState(() => {
    const urlTab = searchParams.get('tab');
    const savedTab = localStorage.getItem('customerDashboardTab');
    return urlTab || savedTab || 'orders';
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // Status filter for orders
  const [showGuidedSetup, setShowGuidedSetup] = useState(false);
  const [showGuidedTour, setShowGuidedTour] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoadingOrders, setIsLoadingOrders] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showTokenExpired, setShowTokenExpired] = useState(false);
  const [appointments, setAppointments] = useState<any[]>([]);
  const [availableTailors, setAvailableTailors] = useState<any[]>([]);
  const [isLoadingAppointments, setIsLoadingAppointments] = useState(false);
  const [selectedTailor, setSelectedTailor] = useState<any>(null);
  const [availableSlots, setAvailableSlots] = useState<any[]>([]);
  const [showAvailableSlots, setShowAvailableSlots] = useState(false);
  
  // Linking code state
  const [showLinkTailor, setShowLinkTailor] = useState(false);
  const [linkingCode, setLinkingCode] = useState('');
  const [isLinking, setIsLinking] = useState(false);
  const [linkingError, setLinkingError] = useState('');
  const [linkingSuccess, setLinkingSuccess] = useState(false);
  
  const handleLinkViaCode = async () => {
    if (!linkingCode.trim() || linkingCode.trim().length !== 6) {
      setLinkingError('Please enter a valid 6-digit code');
      return;
    }

    setIsLinking(true);
    setLinkingError('');
    setLinkingSuccess(false);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/customers/link/code`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          code: linkingCode.trim()
        })
      });

      if (response.ok) {
        const data = await response.json();
        setLinkingSuccess(true);
        setLinkingCode('');
        
        // Refresh tailors list
        await fetchAvailableTailors();
        
        // Hide modal after 2 seconds
        setTimeout(() => {
          setShowLinkTailor(false);
          setLinkingSuccess(false);
        }, 2000);
      } else {
        const errorData = await response.json();
        setLinkingError(errorData.detail || 'Failed to link with tailor. Please check the code and try again.');
      }
    } catch (_error) {
      console.error('Error linking via code:', error);
      setLinkingError('Network error occurred. Please try again.');
    } finally {
      setIsLinking(false);
    }
  };

  const handleScanQR = () => {
    // TODO: Implement QR scanner
    alert('QR scanner functionality coming soon! For now, please enter the code manually.');
  };

  // Payment modal state
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedOrderForPayment, setSelectedOrderForPayment] = useState<Order | null>(null);

  // Profile completion handler
  const handleProfileStepClick = (stepId: string) => {
    switch (stepId) {
      case 'basic_info':
        setActiveTab('profile');
        break;
      case 'address':
        setActiveTab('profile');
        break;
      case 'profile_photo':
        setActiveTab('profile');
        break;
      case 'measurements':
        setActiveTab('measurements');
        break;
      case 'preferences':
        setActiveTab('profile');
        break;
      case 'verification':
        // Navigate to verification page or show verification modal
        alert('Verification feature coming soon!');
        break;
      default:
        console.log('Unknown step:', stepId);
    }
  };

  // Function to handle tab changes and persist them
  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    localStorage.setItem('customerDashboardTab', tabId);
    
    // Update URL params
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('tab', tabId);
    setSearchParams(newSearchParams);
  };

  // Debug logging
  console.log('CustomerDashboard render:', { 
    user, 
    activeTab, 
    orders: orders.length,
    loggedInUser: localStorage.getItem('user'),
    authToken: localStorage.getItem('authToken')
  });

  // Function to attempt token refresh
  const attemptTokenRefresh = async (): Promise<boolean> => {
    try {
      const userData = localStorage.getItem('user');
      if (!userData) return false;
      
      const user = JSON.parse(userData);
      const apiUrl = API_BASE;
      
      // Try to re-authenticate with stored user credentials
      // Note: This is a simplified approach. In production, you'd want to use refresh tokens
      console.log('Attempting to refresh token for user:', user.email);
      
      // For now, we'll just clear the expired token and let the user re-login
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      
      // Redirect to login page
      navigate('/login');
      return false;
    } catch (_error) {
      console.error('Token refresh error:', error);
      return false;
    }
  };

  // TODO: Load real measurements from API
  const [measurements, setMeasurements] = useState<Measurement[]>([]);

  // First-time-only auto-launch tour
  useEffect(() => {
    try {
      if (!localStorage.getItem('tour.customer.shown')) {
        setShowGuidedTour(true);
      }
    } catch (_) {}

    // Clear all cached data to ensure fresh data after database cleanup
    if (user?.email) {
      cacheHelpers.clearUserCache(user.email);
    }
    
    // Clear preloaded data cache to ensure fresh data after database cleanup
    if (user?.id) {
      dataPreloader.clearUserCache(user.id);
    }

    // Skip preloaded data check - always fetch fresh data after database cleanup
    // This ensures we don't use stale cached data
    
    // If no preloaded data, fetch from API
    if (user) {
      fetchOrders();
    }
    
    // Set up global callback for guided setup
    window.guidedSetupCallback = () => setShowGuidedTour(true);
    
    return () => {
      delete window.guidedSetupCallback;
    };
  }, [user?.id]);

  // Load appointments when appointments tab is active
  useEffect(() => {
    if (activeTab === 'appointments' && user?.email) {
      fetchAppointments();
      fetchAvailableTailors();
    }
  }, [activeTab, user?.email]);

  // Filter orders by status
  const filteredOrders = useMemo(() => {
    if (statusFilter === 'all') {
      return orders;
    }
    
    // Handle 'active' filter - show orders that are not completed/delivered/cancelled
    if (statusFilter === 'active') {
      return orders.filter(order => 
        order.status !== 'completed' && 
        order.status !== 'delivered' && 
        order.status !== 'cancelled'
      );
    }
    
    // Filter by specific status
    return orders.filter(order => order.status === statusFilter);
  }, [orders, statusFilter]);

  // Show loading state if user is not loaded
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Fetch orders from backend API (with cache check)
  const fetchOrders = async () => {
    try {
      console.log('fetchOrders started');
      setError(null);
      
      // Clear cache to ensure fresh data after database cleanup
      if (user?.id) {
        cacheHelpers.clearUserCache(user.id);
        dataPreloader.clearUserCache(user.id);
      }
      
      // Fetch from API directly (no cache)
      await fetchOrdersFromAPI();
    } catch (_error) {
      console.error('Error fetching orders:', error);
      setError('Failed to load orders.');
      setOrders([]); // No fallback data
    } finally {
      setIsLoadingOrders(false);
    }
  };

  // Fetch orders directly from API (bypasses cache)
  const fetchOrdersFromAPI = async () => {
    try {
      const apiUrl = API_BASE;
      const token = localStorage.getItem('authToken');
      
      console.log('API URL:', apiUrl, 'Token exists:', !!token);
      
      if (!token) {
        console.log('No auth token found');
        setOrders([]);
        return;
      }

      // Check if token is expired and try to refresh it
      try {
        const tokenParts = token.split('.');
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]));
          const now = Math.floor(Date.now() / 1000);
          if (payload.exp && payload.exp < now) {
            console.log('Token is expired, attempting to refresh...');
            
            // Show token expired message instead of silently failing
            setShowTokenExpired(true);
            setOrders([]);
            return;
            // If refresh successful, continue with the API call
          }
        }
      } catch (tokenError) {
        console.log('Token validation failed');
        setOrders([]);
        return;
      }
      
      // Try authenticated endpoint first
      let ordersResponse;
      try {
        ordersResponse = await fetch(`${apiUrl}/api/orders`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
        });
      } catch (fetchError) {
        console.log('Fetch error (likely network issue):', fetchError);
        setOrders([]);
        return;
      }

      if (ordersResponse.ok) {
        const data = await ordersResponse.json();
        console.log('Web Orders API response:', data);
        
        // Fetch detailed data for each order to get progress_steps
        const detailedOrders = await Promise.all(
          data.orders?.map(async (order: any) => {
            try {
              // Fetch individual order details to get progress_steps
              const detailResponse = await fetch(`${apiUrl}/api/orders/${order.id}`, {
                method: 'GET',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
              });
              
              if (detailResponse.ok) {
                const detailData = await detailResponse.json();
                console.log(`Order ${order.id} detailed data:`, detailData);
                console.log(`Order ${order.id} progress_steps:`, detailData.progress_steps);
                return {
                  ...order,
                  progress_steps: detailData.progress_steps || []
                };
              } else {
                console.log(`Failed to fetch details for order ${order.id}, status:`, detailResponse.status);
                return order;
              }
            } catch (_error) {
              console.log(`Error fetching details for order ${order.id}:`, error);
              return order;
            }
          }) || []
        );
        
        // Convert API data to Order format
        const formattedOrders: Order[] = detailedOrders.map((order: any) => {
          const formattedOrder = {
            id: order.id,
            order_number: order.order_number || order.orderNumber, // Normalize order number
            garmentType: order.garmentType || 'Custom Garment',
            description: order.description || 'Custom order',
            status: order.status || 'pending',
            orderDate: order.orderDate || new Date().toISOString().split('T')[0],
            dueDate: order.dueDate || new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            amount: order.amount || 0,
            tailor: typeof order.tailor === 'object' && order.tailor 
              ? order.tailor 
              : order.tailor || 'Master Tailor Studio',
            measurements: ['Body Measurements', `${order.garmentType || 'Custom'} Measurements`],
            progressSteps: order.progress_steps || [],
            // Include appointment information
            appointment_time: order.appointment_time,
            appointment_date: order.appointment_date,
            appointment_scheduled: order.appointment_scheduled,
            // Include customer information
            customer_name: order.customer_name || order.customer?.name || order.customer?.full_name || user?.name || '',
            customer_phone: order.customer_phone || order.customer?.phone || order.customer?.phone_number || ''
          };
          console.log(`Formatted order ${order.id}:`, formattedOrder);
          return formattedOrder;
        });
        
        setOrders(formattedOrders);
        
        // Cache the successfully fetched orders
        if (user?.id) {
          cacheHelpers.setOrders(user.id, formattedOrders, 5 * 60 * 1000); // 5 minutes TTL
        }
      } else {
        console.log('Authenticated orders API failed:', ordersResponse.status);
        
        // If 401, token might be invalid
        if (ordersResponse.status === 401) {
          console.log('Token appears invalid');
          setOrders([]);
          return;
        }
        
        // No fallback - show empty state
        setOrders([]);
        return;
      }
    } catch (_error) {
      console.error('Error fetching orders:', error);
      setError('Failed to load orders.');
      setOrders([]);
    } finally {
      setIsLoadingOrders(false);
    }
  };

  const getFallbackOrders = (): Order[] => {
    // Return empty array - no fallback mock data
    return [];
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleNewOrder = () => {
    navigate('/customer/orders/new');
  };

  const handleTrackProgress = (orderId: string, orderNumber?: string) => {
    // Use order_number if available, otherwise fallback to ID
    const identifier = orderNumber || orderId;
    navigate(`/order/${identifier}/details`);
  };

  const handleEmailTailor = (order: Order) => {
    // Get tailor email from order data
    const tailorEmail = typeof order.tailor === 'object' && order.tailor?.email 
      ? order.tailor.email 
      : 'mastertailor@masterji.com';
    
    // Create email subject and body
    const subject = `Inquiry about Order #${order.id} - ${order.garmentType}`;
    const body = `Hello,\n\nI would like to inquire about my order:\n\nOrder ID: #${order.id}\nGarment: ${order.garmentType}\nDescription: ${order.description}\nStatus: ${order.status}\n\nPlease let me know about the progress or if you need any additional information.\n\nThank you!`;
    
    // Create mailto link
    const mailtoLink = `mailto:${tailorEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    
    // Open email client
    window.open(mailtoLink, '_blank');
  };

  const handleViewMeasurement = (measurementId: string) => {
    navigate(`/customer/measurements/${measurementId}`);
  };

  const handleDownloadMeasurement = (measurementId: string) => {
    console.log(`Downloading measurement ${measurementId}`);
    alert('Measurement download feature coming soon!');
  };

  const fetchAppointments = async () => {
    if (!user?.email) return;
    
    // Clear any cached appointments to ensure fresh data
    cacheHelpers.clearUserCache(user.email);
    
    setIsLoadingAppointments(true);
    try {
      const apiUrl = API_BASE;
      const token = localStorage.getItem('authToken');
      if (!token) {
        setAppointments([]);
        setIsLoadingAppointments(false);
        return;
      }

      const response = await fetch(`${apiUrl}/api/appointments`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Appointments data:', data);
        const appointmentsData = data.appointments || [];
        setAppointments(appointmentsData);
        // Only cache if we have real data
        if (appointmentsData.length > 0) {
          cacheHelpers.setAppointments(user.email, appointmentsData);
        }
      } else {
        console.log('Failed to fetch appointments:', response.status, response.statusText);
        const errorText = await response.text();
        console.log('Error response:', errorText);
        // No fallback data - show empty state
        setAppointments([]);
      }
    } catch (_error) {
      console.error('Error fetching appointments:', error);
      // No fallback data - show empty state
      setAppointments([]);
    } finally {
      setIsLoadingAppointments(false);
    }
  };

  const fetchAvailableTailors = async () => {
    if (!user?.email) return;
    
    try {
      const apiUrl = API_BASE;
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const response = await fetch(`${apiUrl}/api/appointments/available-tailors`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Available tailors data:', data);
        let tailors = data.tailors || [];
        if ((!tailors || tailors.length === 0) && appointments.length > 0) {
          const inferred = Object.values(
            (appointments as any[]).reduce((acc: any, appt: any) => {
              const key = appt.tailor?.id || appt.tailor?.email || appt.tailor?.name;
              if (!acc[key]) acc[key] = { id: appt.tailor?.id, name: appt.tailor?.name, email: appt.tailor?.email, order_count: 0, has_appointments: true };
              acc[key].order_count += 1;
              return acc;
            }, {})
          );
          tailors = inferred as any;
        }
        setAvailableTailors(tailors as any);
      } else {
        console.log('Failed to fetch available tailors:', response.status, response.statusText);
        const errorText = await response.text();
        console.log('Error response:', errorText);
      }
    } catch (_error) {
      console.error('Error fetching available tailors:', error);
    }
  };

  const handleBookAppointment = () => {
    // For now, redirect to new order form where they can place an order
    // which will automatically schedule an appointment
    navigate('/customer/orders/new');
  };

  const handleTailorClick = async (tailor: any) => {
    // Show available appointments for this tailor
    console.log('ðŸŽ¯ TAILOR CLICKED:', tailor);
    console.log('ðŸŽ¯ This should show in browser console when you click a tailor');
    
    try {
      const apiUrl = API_BASE;

      const token = localStorage.getItem('authToken');
      const response = await fetch(`${apiUrl}/api/appointments/available-slots/${tailor.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('ðŸ“… Available slots:', data);
        
        // Show available slots in a modal or expand the tailor card
        setSelectedTailor(tailor);
        setAvailableSlots(data.available_slots || []);
        setShowAvailableSlots(true);
      } else {
        console.error('Failed to fetch available slots:', response.statusText);
        // No available slots - show empty
        setSelectedTailor(tailor);
        setAvailableSlots([]);
        setShowAvailableSlots(true);
      }
    } catch (_error) {
      console.error('Error fetching available slots:', error);
      // No available slots - show empty
      setSelectedTailor(tailor);
      setAvailableSlots([]);
      setShowAvailableSlots(true);
    }
  };

  const handleSlotClick = (slot: any) => {
    console.log('ðŸŽ¯ SLOT CLICKED:', slot);
    
    // Store the selected appointment details in localStorage for the new order form
    const appointmentData = {
      tailorId: selectedTailor.id,
      tailorName: selectedTailor.name,
      tailorEmail: selectedTailor.email,
      appointmentDate: slot.date,
      appointmentTime: slot.start_time,
      appointmentEndTime: slot.end_time,
      slotId: slot.id
    };
    
    localStorage.setItem('selectedAppointment', JSON.stringify(appointmentData));
    
    // Navigate to new order form with preselected appointment (same route as New Order button)
    navigate('/customer/orders/new');
  };

  const handleNewMessage = () => {
    navigate('/contact');
  };

  const handleViewNotifications = () => {
    navigate('/customer/notifications');
  };

  const handleSettings = () => {
    navigate('/customer/profile');
  };

  // Payment handlers
  const handlePaymentSuccess = () => {
    // Refresh orders after successful payment
    fetchOrders();
    setPaymentModalOpen(false);
    setSelectedOrderForPayment(null);
  };

  const handlePaymentModalClose = () => {
    setPaymentModalOpen(false);
    setSelectedOrderForPayment(null);
  };

  // Get smart status name for display
  const getSmartStatusName = (order: Order) => {
    console.log(`Getting status for order ${order.id}:`, {
      status: order.status,
      progressSteps: order.progressSteps,
      progressStepsLength: order.progressSteps?.length
    });
    
    if (order.progressSteps && Array.isArray(order.progressSteps) && order.progressSteps.length > 0) {
      console.log(`Order ${order.id} progress steps:`, order.progressSteps);
      
      // Find the step that's currently "in_progress"
      const currentStep = order.progressSteps.find(step => step.status === 'in_progress');
      if (currentStep) {
        console.log(`Order ${order.id} current step:`, currentStep.step);
        return currentStep.step;
      }
      
      // If no in_progress step, find the last completed step (current status)
      const completedSteps = order.progressSteps.filter(step => step.status === 'completed');
      console.log(`Order ${order.id} completed steps:`, completedSteps);
      if (completedSteps.length > 0) {
        const lastCompletedStep = completedSteps[completedSteps.length - 1];
        console.log(`Order ${order.id} last completed step:`, lastCompletedStep.step);
        return lastCompletedStep.step;
      }
      
      // If no completed steps, find the first pending step
      const nextStep = order.progressSteps.find(step => step.status === 'pending');
      if (nextStep) {
        console.log(`Order ${order.id} next step:`, nextStep.step);
        return nextStep.step;
      }
      
      // If all steps are completed, show completed
      const allCompleted = order.progressSteps.every(step => step.status === 'completed');
      if (allCompleted) {
        console.log(`Order ${order.id} all steps completed`);
        return 'Completed';
      }
    }
    
    console.log(`Order ${order.id} using fallback status:`, order.status);
    // Fallback to generic status
    return order.status.replace('_', ' ');
  };

  // Order table columns
  const orderColumns: ColumnDef<Order>[] = [
    {
      key: 'id',
      header: 'Order ID',
      searchable: true,
      searchPlaceholder: 'Search order ID...',
      render: (value, row) => (
        <OrderIdBadge orderNumber={(row as any).order_number} id={value} compact />
      )
    },
    {
      key: 'garmentType',
      header: 'Garment',
      searchable: true,
      searchPlaceholder: 'Search garment type...',
      render: (value, row) => (
        <div>
          <div className="font-medium">{value}</div>
          <div className="text-sm text-gray-500">{row.description}</div>
        </div>
      )
    },
    {
      key: 'status',
      header: 'Status',
      searchable: true,
      searchPlaceholder: 'Filter by status...',
      render: (value, row) => {
        const smartStatus = getSmartStatusName(row);
        console.log(`StatusBadge for order ${row.id}:`, {
          originalStatus: row.status,
          smartStatus: smartStatus,
          progressSteps: row.progressSteps
        });
        return (
          <StatusBadge 
            status={smartStatus} 
            type="order" 
            persona="customer"
            showIcon={true}
          />
        );
      }
    },
    {
      key: 'dueDate',
      header: 'Appointment/Due Date',
      searchable: true,
      searchPlaceholder: 'Search date...',
      render: (value, row) => {
        if (row.appointment_scheduled && row.appointment_time) {
          return (
            <div>
              <div className="font-medium text-purple-600">{row.appointment_time}</div>
              <div className="text-sm text-gray-500">Due: {formatDate(value)}</div>
            </div>
          );
        }
        return formatDate(value);
      }
    },
    {
      key: 'amount',
      header: 'Amount',
      searchable: true,
      searchPlaceholder: 'Search amount...',
      render: (value) => formatCurrency(value)
    },
    {
      key: 'tailor',
      header: 'Tailor',
      searchable: true,
      searchPlaceholder: 'Search tailor name...',
      render: (value) => {
        if (typeof value === 'object' && value) {
          return (value as any).name || (value as any).customer_name || 'N/A';
        }
        return value || 'N/A';
      }
    },
    {
      key: 'customer_name',
      header: 'Customer Name',
      searchable: true,
      searchPlaceholder: 'Search customer name...',
      render: (value) => value || 'N/A'
    },
    {
      key: 'customer_phone',
      header: 'Phone Number',
      searchable: true,
      searchPlaceholder: 'Search phone number...',
      render: (value) => value || 'N/A'
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (value, row) => {
        // Only show Pay Now button for unpaid orders
        const isPaid = row.status === 'paid' || (row as any).payment_status === 'paid';
        if (isPaid) {
          return (
            <span className="text-green-600 text-sm font-medium">Paid</span>
          );
        }
        
        return (
          <button
            onClick={() => {
              setSelectedOrderForPayment(row);
              setPaymentModalOpen(true);
            }}
            className="px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
          >
            Pay Now
          </button>
        );
      }
    }
  ];

  // Measurement table columns
  const measurementColumns: ColumnDef<Measurement>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (value, row) => (
        <div>
          <div className="font-medium">{value}</div>
          <div className="text-sm text-gray-500">{row.notes}</div>
        </div>
      )
    },
    {
      key: 'measurementsCount',
      header: 'Progress',
      render: (value, row) => (
        <div className="flex items-center space-x-2">
          <div className="flex-1 bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(value / row.totalMeasurements) * 100}%` }}
            ></div>
          </div>
          <span className="text-sm text-gray-600">{value}/{row.totalMeasurements}</span>
        </div>
      )
    },
    {
      key: 'date',
      header: 'Date',
      render: (value) => formatDate(value)
    },
    {
      key: 'status',
      header: 'Status',
      render: (value) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          value === 'complete' 
            ? 'bg-green-100 text-green-800' 
            : value === 'partial'
            ? 'bg-yellow-100 text-yellow-800'
            : 'bg-red-100 text-red-800'
        }`}>
          {value}
        </span>
      )
    }
  ];

  const orderActions = [
    { label: 'Track', action: 'track', variant: 'primary' as const },
    { label: 'Email', action: 'email', variant: 'secondary' as const }
  ];

  const measurementActions = [
    { label: 'View Details', action: 'view', variant: 'primary' as const },
    { label: 'Download', action: 'download', variant: 'secondary' as const }
  ];

  const handleOrderAction = (action: string, row: Order) => {
    switch (action) {
      case 'track':
        handleTrackProgress(row.id, (row as any).order_number);
        break;
      case 'email':
        handleEmailTailor(row);
        break;
    }
  };

  const handleMeasurementAction = (action: string, row: Measurement) => {
    switch (action) {
      case 'view':
        handleViewMeasurement(row.id);
        break;
      case 'download':
        handleDownloadMeasurement(row.id);
        break;
    }
  };

  return (
    <NotificationProvider>
      <div className="min-h-screen bg-gray-50">
        {/* Enhanced Header */}
        <EnhancedHeader 
          title={`${user?.name || 'Customer'}'s Master Ji`}
          showBackButton={true}
          backTo="/"
          onGuidedSetupClick={() => {
            console.log('Setting showGuidedTour to true');
            setShowGuidedTour(true);
          }}
        />


      {/* Navigation Tabs */}
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            {[
              { id: 'orders', label: 'My Orders', icon: Package },
              { id: 'measurements', label: 'Measurements', icon: FileText },
              { id: 'appointments', label: 'Appointments', icon: Calendar },
              { id: 'receipts', label: 'Receipts', icon: Receipt },
              { id: 'profile', label: 'Profile', icon: User },
              ...(isChatEnabled() ? [{ id: 'messaging', label: 'Messaging', icon: MessageCircle }] : []),
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Error Display */}
        {error && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex">
              <AlertCircle className="w-5 h-5 text-yellow-600 mr-2 mt-0.5" />
              <div>
                <p className="text-yellow-800 font-medium">Notice</p>
                <p className="text-yellow-700 text-sm">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Token Expired Display */}
        {showTokenExpired && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <AlertCircle className="w-5 h-5 text-red-600 mr-2 mt-0.5" />
              <div className="flex-1">
                <p className="text-red-800 font-medium">Session Expired</p>
                <p className="text-red-700 text-sm mb-3">
                  Your login session has expired. Please log in again to access real-time data.
                </p>
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      localStorage.removeItem('authToken');
                      localStorage.removeItem('user');
                      navigate('/login');
                    }}
                    className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors text-sm"
                  >
                    Log In Again
                  </button>
                  <button
                    onClick={() => setShowTokenExpired(false)}
                    className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors text-sm"
                  >
                    Continue with Sample Data
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'orders' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            data-feature="order-tracking"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">My Orders</h2>
              <button 
                data-tour="customer-new-order"
                onClick={handleNewOrder}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4 mr-2 inline" />
                New Order
              </button>
            </div>

            {/* Status Filter */}
            <div className="mb-4 bg-white rounded-lg shadow p-4">
              <div className="flex items-center gap-4">
                <label className="block text-sm font-medium text-gray-700">
                  Filter by Status:
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900"
                >
                  <option value="all">All Orders</option>
                  <option value="active">Active Orders</option>
                  <option value="pending">Pending</option>
                  <option value="in_progress">In Progress</option>
                  <option value="appointment_scheduled">Appointment Scheduled</option>
                  <option value="ready">Ready for Pickup</option>
                  <option value="completed">Completed</option>
                  <option value="delivered">Delivered</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                {statusFilter !== 'all' && (
                  <span className="text-sm text-gray-600">
                    Showing {filteredOrders.length} {filteredOrders.length === 1 ? 'order' : 'orders'}
                  </span>
                )}
              </div>
            </div>

            {isLoadingOrders ? (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="animate-pulse space-y-4">
                  <div className="h-6 bg-gray-200 rounded w-1/3"></div>
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                  <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                  <div className="h-4 bg-gray-200 rounded w-4/6"></div>
                  <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                </div>
                <p className="text-sm text-gray-500 mt-4">Fetching your ordersâ€¦</p>
              </div>
            ) : (
              <DataTable
                data={filteredOrders}
                columns={orderColumns}
                persona="customer"
                searchable={false} // Disable global search since we have column-specific search
                filterable={false} // Disable DataTable filter button since we have custom filter
                pagination={true}
                actions={orderActions}
                onAction={handleOrderAction}
                emptyMessage={statusFilter !== 'all' ? `No ${statusFilter === 'active' ? 'active' : statusFilter.replace('_', ' ')} orders found.` : "No orders found."}
              />
            )}

          </motion.div>
        )}

        {activeTab === 'measurements' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">My Measurements</h2>
                <p className="text-gray-600 mt-1">Measurements taken by your tailors</p>
              </div>
            </div>

            {/* Measurements from Different Tailors */}
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow p-8 text-center">
                <div className="text-6xl mb-4">📏</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No Measurements Yet</h3>
                <p className="text-gray-600 mb-4">
                  Your measurements will appear here after your tailor takes them during an appointment.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-blue-800 text-sm">
                    <strong>Next Step:</strong> Book an appointment with a tailor to get your measurements taken.
                  </p>
                </div>
              </div>
            </div>

          </motion.div>
        )}

        {activeTab === 'appointments' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Appointments</h2>
                <p className="text-gray-600 mt-1">Book appointments with your tailors</p>
              </div>
              <button 
                onClick={handleBookAppointment}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4 mr-2 inline" />
                Book Appointment
              </button>
            </div>

            {/* Link with Tailor Section */}
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Link with a Tailor</h3>
                <button
                  onClick={() => setShowLinkTailor(!showLinkTailor)}
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1"
                >
                  {showLinkTailor ? (
                    <>
                      <XCircle className="w-4 h-4" />
                      Hide
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      Link New Tailor
                    </>
                  )}
                </button>
              </div>
              
              {showLinkTailor && (
                <div className="space-y-4 border-t pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Code Entry */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        <KeyRound className="w-4 h-4 inline mr-1" />
                        Enter Linking Code
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={linkingCode}
                          onChange={(e) => {
                            setLinkingCode(e.target.value.toUpperCase().slice(0, 6));
                            setLinkingError('');
                          }}
                          placeholder="ABC123"
                          maxLength={6}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-center text-lg"
                        />
                        <button
                          onClick={handleLinkViaCode}
                          disabled={isLinking || linkingCode.length !== 6}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isLinking ? 'Linking...' : 'Link'}
                        </button>
                      </div>
                      <p className="text-xs text-gray-500">Ask your tailor for a 6-character linking code</p>
                    </div>
                    
                    {/* QR Scanner */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        <QrCode className="w-4 h-4 inline mr-1" />
                        Scan QR Code
                      </label>
                      <button
                        onClick={handleScanQR}
                        className="w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-gray-600"
                      >
                        <QrCode className="w-5 h-5 inline mr-2" />
                        Scan QR Code
                      </button>
                      <p className="text-xs text-gray-500">Scan the QR code provided by your tailor</p>
                    </div>
                  </div>
                  
                  {linkingError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                      <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-800">{linkingError}</p>
                    </div>
                  )}
                  
                  {linkingSuccess && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-green-800">Successfully linked with tailor!</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Available Tailors */}
            <div className="bg-white rounded-lg shadow p-6 mb-6" data-tour="customer-appointments-section">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Tailors</h3>
              {isLoadingAppointments ? (
                <div className="text-center py-4">
                  <div className="text-gray-500">Loading tailors...</div>
                </div>
              ) : availableTailors.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {availableTailors.map((tailor, index) => (
                    <div 
                      key={tailor.id} 
                      className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer hover:border-blue-300 hover:bg-blue-50"
                      onClick={() => handleTailorClick(tailor)}
                      title="Click to see available appointments"
                    >
                      <div className="flex items-center space-x-3 mb-3">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                          index % 3 === 0 ? 'bg-blue-100' : index % 3 === 1 ? 'bg-green-100' : 'bg-purple-100'
                        }`}>
                          <span className={`font-semibold ${
                            index % 3 === 0 ? 'text-blue-600' : index % 3 === 1 ? 'text-green-600' : 'text-purple-600'
                          }`}>
                            {tailor.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                          </span>
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{tailor.name}</div>
                          <div className="text-sm text-gray-600">{tailor.order_count} order{tailor.order_count !== 1 ? 's' : ''}</div>
                        </div>
                      </div>
                      <div className="text-sm text-gray-600 mb-2">
                        <div>ðŸ“§ {tailor.email}</div>
                        <div className={`text-sm font-medium ${
                          tailor.has_appointments ? 'text-green-600' : 'text-gray-500'
                        }`}>
                          {tailor.has_appointments ? 'Has scheduled appointments' : 'No appointments scheduled'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="text-gray-500 mb-4">No tailors found</div>
                  <div className="text-sm text-gray-400">Place an order to see your tailors here</div>
                </div>
              )}
            </div>

            {/* Available Appointment Slots */}
            {showAvailableSlots && selectedTailor && (
              <div className="bg-white rounded-lg shadow p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Available Times with {selectedTailor.name}
                  </h3>
                  <button
                    onClick={() => setShowAvailableSlots(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    âœ•
                  </button>
                </div>
                
                {availableSlots.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {availableSlots.map((slot) => (
                      <div
                        key={slot.id}
                        className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer hover:border-blue-300"
                        onClick={() => handleSlotClick(slot)}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium text-gray-900">
                              {new Date(slot.date).toLocaleDateString('en-US', {
                                weekday: 'long',
                                month: 'short',
                                day: 'numeric'
                              })}
                            </div>
                            <div className="text-sm text-gray-500">
                              {slot.start_time} - {slot.end_time}
                            </div>
                          </div>
                          <div className="text-green-600 text-sm font-medium">
                            Available
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="text-gray-500">No available slots found</div>
                    <div className="text-sm text-gray-400">
                      Please try again later or contact the tailor directly
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Upcoming Appointments */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Appointments</h3>
              {isLoadingAppointments ? (
                <div className="text-center py-4">
                  <div className="text-gray-500">Loading appointments...</div>
                </div>
              ) : appointments.length > 0 ? (
                <div className="space-y-4">
                  {Object.values(
  appointments.reduce((acc: any, appt: any) => {
    const key = appt.tailor?.id || appt.tailor?.email || appt.tailor?.name;
    if (!acc[key]) acc[key] = { tailor: appt.tailor, items: [] as any[] };
    acc[key].items.push(appt);
    return acc;
  }, {})
).map((group: any, groupIndex: number) => (
  <div key={group.tailor.id || group.tailor.email || groupIndex} className="p-4 border border-gray-200 rounded-lg">
    <div className="flex items-center space-x-3 mb-2">
      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
        groupIndex % 3 === 0 ? 'bg-blue-100' : groupIndex % 3 === 1 ? 'bg-green-100' : 'bg-purple-100'
      }`}>
        <span className={`font-semibold ${
          groupIndex % 3 === 0 ? 'text-blue-600' : groupIndex % 3 === 1 ? 'text-green-600' : 'text-purple-600'
        }`}>
          {group.tailor.name.split(' ').map((n: string) => n[0]).join('').substring(0, 2)}
        </span>
      </div>
      <div>
        <div className="font-medium text-gray-900">{group.tailor.name}</div>
        <div className="text-sm text-gray-500">{group.items.length} appointment{group.items.length !== 1 ? 's' : ''}</div>
      </div>
    </div>
    <div className="divide-y divide-gray-200">
      {group.items.map((appointment: any, idx: number) => (
        <div key={appointment.id || idx} className="flex items-center justify-between py-3">
          <div>
            <div className="text-sm text-gray-600 flex items-center space-x-2">
              <span>{appointment.service_type} •</span>
              <span>
                <OrderIdBadge orderNumber={appointment.order_number} compact />
              </span>
            </div>
            <div className="text-sm text-gray-500">
              {appointment.appointment_date ? 
                new Date(appointment.appointment_date).toLocaleDateString('en-US', {
                  weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
                }) : 'Date TBD'}
            </div>
          </div>
          <div className="flex space-x-2">
            <button className="px-3 py-1 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors text-sm">
              View Order
            </button>
            <button className="px-3 py-1 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors text-sm">
              Reschedule
            </button>
          </div>
        </div>
      ))}
    </div>
  </div>
))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="text-gray-500 mb-4">No appointments scheduled</div>
                  <div className="text-sm text-gray-400">
                    Click on a tailor above to see available appointment times
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {activeTab === 'receipts' && (
          <ReceiptsTab userRole="customer" />
        )}

        {activeTab === 'profile' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Profile Management</h2>
                <p className="text-gray-600 mt-1">Manage your personal information and profile settings</p>
              </div>
            </div>

            {/* Profile Completion */}
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <ProfileCompletion 
                userRole="customer"
                onStepClick={handleProfileStepClick}
                showDetailed={true}
                compact={false}
              />
            </div>

          </motion.div>
        )}

        {isChatEnabled() && activeTab === 'messaging' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            data-tour="customer-messaging-entry"
          >
            {console.log('Rendering MessagingPage component')}
            <MessagingPage />
          </motion.div>
        )}
      </main>

      {/* Guided Setup Modal */}
      {(showGuidedSetup || showGuidedTour) && user && (
        <>
          <GuidedTour role="customer" open={showGuidedTour || showGuidedSetup} onClose={() => { 
            setShowGuidedTour(false); 
            setShowGuidedSetup(false);
            try { localStorage.setItem('tour.customer.shown','1'); } catch (_) {} 
          }} />
        </>
      )}

      {/* Settings Modal */}
      <SettingsModal 
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
      />

      {/* Payment Modal */}
      {selectedOrderForPayment && (
        <OrderPaymentModal
          isOpen={paymentModalOpen}
          onClose={handlePaymentModalClose}
          order={{
            id: parseInt(selectedOrderForPayment.id),
            order_number: selectedOrderForPayment.order_number || selectedOrderForPayment.id,
            amount: selectedOrderForPayment.amount,
            description: selectedOrderForPayment.description,
            garment_type: selectedOrderForPayment.garmentType
          }}
          onPaymentSuccess={handlePaymentSuccess}
        />
      )}
      </div>
    </NotificationProvider>
  );
};

export default CustomerDashboard;
